import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { hasRole, hasWorkspace, useSessionStore, type Role } from '@/shared/api';

import { homeFor } from './home';
import { paths } from './paths';

/**
 * Blokkeert een route tot er een sessie is. Zolang de sessie nog uit IndexedDB
 * wordt gelezen tonen we niets - dat duurt milliseconden en een tussenscherm
 * zou als flikkering zichtbaar zijn.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
    const session = useSessionStore((state) => state.session);
    const hydrated = useSessionStore((state) => state.hydrated);
    const location = useLocation();

    if (!hydrated) {
        return null;
    }

    if (!session) {
        return (
            <Navigate
                to={paths.login}
                replace
                state={{ from: `${location.pathname}${location.search}` }}
            />
        );
    }

    return <>{children}</>;
}

/**
 * De schermen van een keuringsbedrijf hebben een omgeving nodig. Een
 * platformbeheerder zonder omgeving gaat naar het platformbeheer; iemand met
 * meerdere bedrijven en nog geen keuze naar de omgevingskeuze.
 */
export function RequireWorkspace({ children }: { children: ReactNode }) {
    const session = useSessionStore((state) => state.session);
    const impersonation = useSessionStore((state) => state.impersonation);

    if (session && !hasWorkspace(session, impersonation)) {
        return <Navigate to={homeFor(session, impersonation)} replace />;
    }

    return <RequireAuth>{children}</RequireAuth>;
}

/**
 * Rolgebaseerde toegang. Een platformbeheerder komt overal binnen (zie
 * `hasRole`).
 */
export function RequireRole({ roles, children }: { roles: readonly Role[]; children: ReactNode }) {
    const session = useSessionStore((state) => state.session);
    const impersonation = useSessionStore((state) => state.impersonation);
    const hydrated = useSessionStore((state) => state.hydrated);

    if (!hydrated) {
        return null;
    }

    if (!hasRole(session, roles)) {
        return <Navigate to={session ? homeFor(session, impersonation) : paths.login} replace />;
    }

    return <>{children}</>;
}

/**
 * Wie al is ingelogd hoeft het inlogscherm niet te zien: door naar waar hij
 * vandaan kwam, of naar zijn begin. Een sessie zonder omgeving blijft hier - het
 * inlogscherm is dan de omgevingskeuze.
 */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
    const session = useSessionStore((state) => state.session);
    const impersonation = useSessionStore((state) => state.impersonation);
    const hydrated = useSessionStore((state) => state.hydrated);
    const location = useLocation();

    if (!hydrated || !session) {
        return <>{children}</>;
    }

    const home = homeFor(session, impersonation);

    if (home === paths.login) {
        return <>{children}</>;
    }

    const from = returnPath(location.state);
    const target = from && (home === paths.dashboard || from.startsWith(paths.admin)) ? from : home;

    return <Navigate to={target} replace />;
}

/** Alleen een pad binnen de app, nooit een adres elders. */
function returnPath(state: unknown): string | null {
    if (typeof state !== 'object' || state === null || !('from' in state)) {
        return null;
    }

    const from = (state as { from: unknown }).from;

    return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : null;
}
